import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import NotificationBell from './NotificationBell';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, userProfile, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      label: 'Overview',
      path: '/compliance-overview',
      icon: 'LayoutDashboard',
      description: 'Compliance overview and organizational metrics'
    },
    {
      label: 'Management',
      path: '/employee-management',
      icon: 'UserCog',
      description: 'Employee records management and organizational structure'
    },
    {
      label: 'PTO',
      path: '/pto-management',
      icon: 'CalendarDays',
      description: 'Track and manage employee paid time off'
    },
    {
      label: 'Work Orders',
      path: '/work-order-tracker',
      icon: 'ClipboardList',
      description: 'HxGN Work Order Tracker - import, manage, and track work orders'
    },
    {
      label: 'Audit Log',
      path: '/audit-log',
      icon: 'FileText',
      description: 'Comprehensive tracking of all system changes and activities'
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      description: 'System configuration and compliance rule management'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-navigation bg-card elevation-md">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg">
              <img
                src="/assets/images/Real_logo-1771587686730.png"
                alt="REAL Network Services Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-heading font-semibold text-foreground">
                ComplianceTracker
              </span>
              <span className="text-xs font-caption text-muted-foreground hidden sm:block">
                Employee Compliance Management
              </span>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg
                  transition-smooth font-body font-medium
                  ${isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
                title={item?.description}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
              </Link>
            ))}
            <a href="/violation-records" className={`px-4 py-2 rounded-lg transition-colors ${isActive('/violation-records') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
              Violation Records
            </a>
            {isAdmin && (
              <Link
                to="/user-management"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-smooth font-body font-medium ${
                  isActive('/user-management')
                    ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted hover:text-foreground'
                }`}
                title="Manage system users"
              >
                <Icon name="Users" size={20} />
                <span>Users</span>
              </Link>
            )}
            <NotificationBell />
            {/* User info + logout */}
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
              {userProfile && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary">
                      {userProfile?.full_name?.charAt(0)?.toUpperCase() || userProfile?.email?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden xl:flex flex-col">
                    <span className="text-xs font-medium text-foreground leading-tight">{userProfile?.full_name || userProfile?.email}</span>
                    <span className="text-xs text-muted-foreground leading-tight">{userProfile?.role}</span>
                  </div>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                iconName="LogOut"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </nav>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-dropdown bg-background lg:hidden"
          style={{ top: '64px' }}
        >
          <nav className="flex flex-col p-4 gap-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={closeMobileMenu}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-smooth font-body font-medium
                  ${isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item?.icon} size={20} />
                <div className="flex flex-col">
                  <span>{item?.label}</span>
                  <span className="text-xs text-muted-foreground mt-0.5">{item?.description}</span>
                </div>
              </Link>
            ))}
            <a href="/violation-records" onClick={closeMobileMenu} className={`px-4 py-3 rounded-lg transition-colors ${isActive('/violation-records') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
              Violation Records
            </a>
            {isAdmin && (
              <Link
                to="/user-management"
                onClick={closeMobileMenu}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth font-body font-medium ${
                  isActive('/user-management')
                    ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Users" size={20} />
                <span>User Management</span>
              </Link>
            )}
            {userProfile && (
              <div className="px-4 py-3 border-t border-border mt-2">
                <p className="text-sm font-medium text-foreground">{userProfile?.full_name || userProfile?.email}</p>
                <p className="text-xs text-muted-foreground">{userProfile?.role}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="LogOut"
              onClick={handleLogout}
              fullWidth
              className="mt-2"
            >
              Logout
            </Button>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;